import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

export function PrivacyPage() {
  return (
    <div className="min-h-dvh bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-[20px]"
            style={{ fontFamily: "'Oleo Script', cursive" }}
          >
            <span className="text-[#688192]">K-Beauty</span>
            <span className="text-[#46C0C6]"> AI</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#688192] hover:text-[#46C0C6] transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <motion.main
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto px-4 py-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">
          Effective Date: January 12, 2026 | Last Updated: January 23, 2026
        </p>

        <div className="prose prose-gray max-w-none">
          <p className="text-gray-600 mb-6">
            Aday Inc. ("Company," "we," "us," or "our") operates K-Skin AI ("Service") at k-skin.ai.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
          </p>

          <Section title="1. Information We Collect">
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Information You Provide</h3>

            <p><strong>Account Information</strong></p>
            <ul>
              <li>Email address (via Google or Apple Sign-In)</li>
              <li>Display name (from your authentication provider)</li>
              <li>Profile photo URL (from your authentication provider)</li>
            </ul>

            <p className="mt-4"><strong>Survey Responses</strong></p>
            <ul>
              <li>Age, skin type, sensitivity level</li>
              <li>Skin concerns and goals</li>
              <li>Product preferences (texture, finish)</li>
              <li>Budget and ingredient preferences</li>
            </ul>

            <p className="mt-4"><strong>Photos</strong></p>
            <ul>
              <li>Selfie photo uploaded for AI skin analysis</li>
              <li>Photos are used solely for generating your skin analysis</li>
            </ul>

            <p className="mt-4"><strong>Payment Information</strong></p>
            <ul>
              <li>We do not store your credit card information</li>
              <li>Payment is processed securely through Stripe</li>
              <li>We receive payment confirmation metadata only</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-6 mb-2">Information Collected Automatically</h3>
            <ul>
              <li>Browser type and device information</li>
              <li>IP address (for geo-location verification)</li>
              <li>Pages visited and actions taken</li>
              <li>Analytics data for Service improvement</li>
            </ul>
          </Section>

          <Section title="2. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul>
              <li><strong>Provide our Service:</strong> Analyze your skin and generate recommendations</li>
              <li><strong>Process payments:</strong> Complete your purchase through Stripe</li>
              <li><strong>Communicate with you:</strong> Send your PDF report, respond to inquiries</li>
              <li><strong>Improve our Service:</strong> Analyze usage patterns</li>
              <li><strong>Ensure security:</strong> Detect and prevent fraud</li>
            </ul>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 my-4">
              <p className="text-green-800 text-sm">
                <strong>We do NOT:</strong> Sell your personal data, send unsolicited marketing emails, or make medical diagnoses.
              </p>
            </div>
          </Section>

          <Section title="3. Data Retention">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">User Type</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Data Retention</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-gray-600">Anonymous (Free Teaser)</td>
                    <td className="px-4 py-3 text-gray-600">Photo deleted after teaser analysis; survey data stored locally only</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-gray-600">Registered (Paid)</td>
                    <td className="px-4 py-3 text-gray-600">Data retained until you request deletion</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="4. How We Share Your Information">
            <p>We share your information only with these third parties:</p>

            <p className="mt-4"><strong>Service Providers</strong></p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Provider</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-gray-600">PerfectCorp</td>
                    <td className="px-4 py-3 text-gray-600">AI skin analysis</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-gray-600">Stripe</td>
                    <td className="px-4 py-3 text-gray-600">Payment processing</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-gray-600">Firebase (Google)</td>
                    <td className="px-4 py-3 text-gray-600">Authentication</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-gray-600">Amazon Web Services</td>
                    <td className="px-4 py-3 text-gray-600">Cloud hosting & storage</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-gray-600">Amazon SES</td>
                    <td className="px-4 py-3 text-gray-600">Email delivery</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mt-4"><strong>Analytics Partners</strong></p>
            <ul>
              <li>Google Analytics 4 - Website analytics</li>
              <li>TikTok Pixel - Advertising analytics</li>
              <li>AppsFlyer - Attribution tracking</li>
            </ul>
          </Section>

          <Section title="5. Data Security">
            <p>We implement appropriate security measures:</p>
            <ul>
              <li><strong>Encryption:</strong> All data transmitted using HTTPS/TLS</li>
              <li><strong>Secure Storage:</strong> Photos in private AWS S3 buckets</li>
              <li><strong>Authentication:</strong> Firebase JWT token validation</li>
              <li><strong>Access Control:</strong> IAM least-privilege policies</li>
              <li><strong>Secure Payments:</strong> Stripe handles all card data (PCI DSS compliant)</li>
            </ul>
          </Section>

          <Section title="6. Your Privacy Rights">
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access:</strong> View your analysis results and download your PDF</li>
              <li><strong>Delete a Single Analysis:</strong> Remove a specific analysis and its data</li>
              <li><strong>Delete All Analyses:</strong> Remove your entire analysis history</li>
              <li><strong>Delete Your Account:</strong> Permanently remove all your data</li>
            </ul>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
              <p className="text-amber-800 text-sm">
                <strong>Important:</strong> Deletion is irreversible. Once deleted, your data cannot be recovered.
              </p>
            </div>

            <p>
              To exercise your rights, use account settings in the app or email <strong>support@k-skin.ai</strong>.
            </p>
          </Section>

          <Section title="7. Children's Privacy">
            <p>
              Our Service is not intended for users under 16 years of age. We do not knowingly collect personal information
              from children under 16. If you believe a child has provided us with personal information, please contact us
              at <strong>support@k-skin.ai</strong>.
            </p>
          </Section>

          <Section title="8. Geographic Restrictions">
            <p>
              Our Service is currently available only in the <strong>United States</strong>. We implement geo-blocking
              to restrict access from other countries.
            </p>
          </Section>

          <Section title="9. Cookies and Tracking">
            <p>We use cookies for:</p>
            <ul>
              <li><strong>Essential:</strong> Authentication, session management</li>
              <li><strong>Analytics:</strong> Understanding Service usage</li>
              <li><strong>Marketing:</strong> Ad conversion tracking (TikTok Pixel)</li>
            </ul>
            <p className="mt-2">
              We also use browser local storage to save your survey responses and analysis results for seamless navigation.
            </p>
          </Section>

          <Section title="10. Third-Party Links">
            <p>
              Our Service contains affiliate links to third-party retailers (Amazon, Olive Young, etc.).
              We are not responsible for their privacy practices. Please review their privacy policies.
            </p>
          </Section>

          <Section title="11. California Privacy Rights (CCPA)">
            <p>California residents have additional rights:</p>
            <ul>
              <li><strong>Right to Know:</strong> Request information about data we collect</li>
              <li><strong>Right to Delete:</strong> Request deletion of your data</li>
              <li><strong>Right to Non-Discrimination:</strong> We won't discriminate for exercising rights</li>
              <li><strong>Right to Opt-Out of Sale:</strong> We do NOT sell your personal information</li>
            </ul>
          </Section>

          <Section title="12. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. We will notify you by posting the updated policy
              and updating the "Last Updated" date. Significant changes will be communicated via email.
            </p>
          </Section>

          <Section title="13. Contact Us">
            <p>For questions about this Privacy Policy, contact us at:</p>
            <p className="mt-2">
              <strong>Aday Inc.</strong><br />
              Email: support@k-skin.ai<br />
              Website: https://k-skin.ai
            </p>
          </Section>

          {/* Summary Table */}
          <div className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Summary of Key Points</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="py-2 pr-4 font-medium text-gray-700">Data Collected</td>
                    <td className="py-2 text-gray-600">Photos, survey answers, email, payment metadata</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium text-gray-700">Photo Handling</td>
                    <td className="py-2 text-gray-600">Anonymous: deleted after teaser; Paid: stored until you delete</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium text-gray-700">Third Parties</td>
                    <td className="py-2 text-gray-600">PerfectCorp, Stripe, Firebase, AWS</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium text-gray-700">Your Rights</td>
                    <td className="py-2 text-gray-600">Access, delete analysis, delete account</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium text-gray-700">Security</td>
                    <td className="py-2 text-gray-600">HTTPS, private S3, JWT auth, no card storage</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium text-gray-700">Age Requirement</td>
                    <td className="py-2 text-gray-600">16+ years old</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium text-gray-700">Location</td>
                    <td className="py-2 text-gray-600">U.S. only</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 italic">
              By using K-Skin AI, you acknowledge that you have read and understood this Privacy Policy.
            </p>
          </div>
        </div>
      </motion.main>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
      <div className="text-gray-600 space-y-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_li]:text-gray-600">
        {children}
      </div>
    </div>
  )
}
